import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bookKey,
  can,
  getBook,
  getEditionPlan,
  listGroups,
  publishBook,
  setBookVisibility,
  type EditionChange,
  type ReadableBy,
} from "../api";
import { useT, type Messages } from "../i18n";
import { useSession } from "../session";

/**
 * EditionPanel is where the studio's work becomes the readers' book (ADR-0024).
 *
 * Everything done in the studio — edits, renames, new chapters, new audio —
 * stays there until it is published as an edition. This panel shows what
 * readers have, what they would get, and what would go out with audio that no
 * longer matches its text, before the button that does it. Nothing is
 * published by saving.
 */
export default function EditionPanel({ bookID, bare = false }: { bookID: string; bare?: boolean }) {
  const t = useT();
  const client = useQueryClient();
  const [note, setNote] = useState("");
  // Who the first edition is for; a published book's own visibility is the
  // server's, read below (ADR-0152).
  const [firstReaders, setFirstReaders] = useState<ReadableBy>("public");
  // And which groups, when it is for groups (ADR-0153).
  const [firstGroups, setFirstGroups] = useState<string[]>([]);

  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  const published = book.data?.published ?? false;
  const readableBy: ReadableBy = published
    ? book.data?.visibility === "public" || book.data?.visibility === "shared"
      ? book.data.visibility
      : "private"
    : firstReaders;
  const chosenGroups = published ? (book.data?.shared_with ?? []) : firstGroups;

  // The book's own library's groups: a book is shared only with those.
  const { me } = useSession();
  const org = me?.orgs.find((o) => o.id === book.data?.org_id);
  const canShare = can(org, "group.manage");
  const groups = useQuery({
    queryKey: ["groups", org?.slug],
    queryFn: () => listGroups(org!.slug),
    enabled: !!org && canShare,
  });

  const plan = useQuery({
    queryKey: ["edition", bookID],
    queryFn: () => getEditionPlan(bookID),
    // A comparison of two moving sets of pointers: coming back from narrating
    // a chapter must not show the plan from before it.
    staleTime: 0,
  });

  const publish = useMutation({
    // A republish names nothing, so it keeps the book's visibility; only the
    // first edition carries the choice.
    mutationFn: () =>
      published
        ? publishBook(bookID, true, note.trim())
        : publishBook(
            bookID,
            true,
            note.trim(),
            firstReaders,
            firstReaders === "shared" ? firstGroups : undefined,
          ),
    onSuccess: async () => {
      setNote("");
      await Promise.all(
        [["edition", bookID], ["book", bookID], ["books"], ["chapters", bookID]].map((queryKey) =>
          client.invalidateQueries({ queryKey }),
        ),
      );
    },
  });

  const visibility = useMutation({
    mutationFn: ({ to, groupIDs }: { to: ReadableBy; groupIDs?: string[] }) =>
      setBookVisibility(bookID, to, groupIDs),
    onSuccess: (updated) => {
      client.setQueryData(bookKey(bookID, { draft: true }), updated);
      void client.invalidateQueries({ queryKey: ["books"] });
      void client.invalidateQueries({ queryKey: ["book", bookID] });
    },
  });

  const choose = (to: ReadableBy) => {
    if (to === readableBy) return;
    if (published) visibility.mutate({ to });
    else setFirstReaders(to);
  };

  // One press shares or unshares with a group; on a published book it takes
  // effect at once, with no new edition, as the visibility does.
  const toggleGroup = (id: string) => {
    const next = chosenGroups.includes(id)
      ? chosenGroups.filter((g) => g !== id)
      : [...chosenGroups, id];
    if (published) visibility.mutate({ to: "shared", groupIDs: next });
    else setFirstGroups(next);
  };
  const readerChoices: ReadableBy[] = canShare
    ? ["public", "shared", "private"]
    : ["public", "private"];

  const p = plan.data;
  if (!p) return null;

  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(t.meta.dateLocale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  const warned = p.changes.filter(
    (c) => c.kind !== "removed" && (c.no_audio || c.audio_outdated),
  ).length;
  const upToDate =
    !!p.published &&
    p.changes.length === 0 &&
    !p.characters_changed &&
    !p.cover_changed &&
    !(p.speakers_changed > 0) &&
    !((p.speakers_lost ?? 0) > 0);

  return (
    <section data-testid="edition-panel" className={bare ? "" : "mt-10 border-t border-line pt-5"}>
      {/* On its own screen the page heading says what this is (BookToolPage). */}
      {!bare && <h2 className="text-sm font-medium">{t.edition.heading}</h2>}
      <p className="mt-1 text-xs text-muted" data-testid="edition-status">
        {p.published
          ? t.edition.published(p.published.number, date(p.published.published_at))
          : t.edition.notPublished}
      </p>

      {/* Who may read it (ADR-0152). Before the first edition it is what that
          edition will be; after, one press changes it, with no new edition. */}
      <div className="mt-3" data-testid="who-can-read" data-visibility={readableBy}>
        <p className="text-xs font-medium text-ink-soft">{t.edition.whoCanRead}</p>
        <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={t.edition.whoCanRead}>
          {readerChoices.map((v) => (
            <button
              key={v}
              type="button"
              data-testid={`readable-by-${v}`}
              aria-pressed={v === readableBy}
              disabled={!book.data || visibility.isPending}
              onClick={() => choose(v)}
              className={`rounded-full border px-4 py-2 text-sm transition-colors disabled:opacity-50 ${
                v === readableBy
                  ? "border-sage bg-sage-soft font-medium"
                  : "border-line hover:bg-sunk"
              }`}
            >
              {v === "public"
                ? t.edition.everyone
                : v === "shared"
                  ? t.edition.groups
                  : t.edition.onlyYou}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-muted">
          {readableBy === "public"
            ? t.edition.everyoneNote
            : readableBy === "shared"
              ? t.edition.groupsNote
              : t.edition.onlyYouNote}
        </p>
        {readableBy === "shared" && groups.data && (
          <div className="mt-2" data-testid="share-groups">
            {groups.data.items.length === 0 ? (
              <p className="text-xs text-muted">{t.edition.noGroupsYet}</p>
            ) : (
              <div className="flex flex-wrap gap-2" role="group" aria-label={t.edition.groups}>
                {groups.data.items.map((g) => {
                  const on = chosenGroups.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      data-testid="share-group"
                      aria-pressed={on}
                      disabled={visibility.isPending}
                      onClick={() => toggleGroup(g.id)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-50 ${
                        on ? "border-sage bg-sage-soft font-medium" : "border-line hover:bg-sunk"
                      }`}
                    >
                      <bdi>{g.name}</bdi>
                    </button>
                  );
                })}
              </div>
            )}
            {groups.data.items.length > 0 && chosenGroups.length === 0 && (
              <p
                className="mt-1 text-xs text-amber-700 dark:text-amber-300"
                data-testid="no-group-chosen"
              >
                {t.edition.noGroupChosen}
              </p>
            )}
            <Link
              to="/studio/groups"
              data-testid="manage-groups"
              className="mt-2 inline-block text-xs text-sage-deep underline-offset-2 hover:underline"
            >
              {t.edition.manageGroups}
            </Link>
          </div>
        )}
        {visibility.isError && (
          <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
            {t.edition.visibilityFailed}
          </p>
        )}
      </div>

      {upToDate ? (
        <p className="mt-3 text-sm text-ink-soft" data-testid="edition-up-to-date">
          {t.edition.upToDate}
        </p>
      ) : (
        <>
          {p.changes.length > 0 && (
            <>
              <p className="mt-3 text-xs font-medium text-ink-soft">
                {t.edition.changes(p.changes.length)}
              </p>
              <ul
                data-testid="edition-changes"
                className="mt-2 divide-y divide-line rounded-card border border-line bg-surface"
              >
                {p.changes.map((change) => (
                  <ChangeRow key={change.chapter_id} change={change} t={t} />
                ))}
              </ul>
            </>
          )}

          {p.characters_changed && (
            <p className="mt-3 text-xs text-ink-soft" data-testid="edition-characters-changed">
              {t.edition.charactersChanged}
            </p>
          )}

          {/* The cover is published like everything else a reader is served
              (ADR-0062). It has no chapter to hang under, so it is its own
              line, beside the cast's. */}
          {p.cover_changed && (
            <p className="mt-3 text-xs text-ink-soft" data-testid="edition-cover-changed">
              {t.edition.coverChanged}
            </p>
          )}

          {/* Who says each quote is published too (ADR-0126): a correction in
              the studio reaches readers only from here. */}
          {p.speakers_changed > 0 && (
            <p className="mt-3 text-xs text-ink-soft" data-testid="edition-speakers-changed">
              {t.edition.speakersChanged(p.speakers_changed)}
            </p>
          )}

          {/* A warning, not a refusal (ADR-0195): an edit the studio could
              not follow leaves these lines with no speaker until Update. */}
          {(p.speakers_lost ?? 0) > 0 && (
            <p
              data-testid="edition-speakers-lost"
              className="mt-3 rounded-control bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            >
              {t.edition.speakersLost(p.speakers_lost ?? 0)}
            </p>
          )}

          {/* Said before the button, not after: publishing text over audio
              recorded from earlier text is allowed, and is the kind of thing
              nobody means to do. */}
          {warned > 0 && (
            <p
              data-testid="edition-warning"
              className="mt-3 rounded-control bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            >
              {t.edition.warning(warned)}
            </p>
          )}

          <input
            dir="auto"
            value={note}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t.edition.notePlaceholder}
            aria-label={t.edition.note}
            data-testid="edition-note"
            className="mt-3 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm"
          />
          <button
            type="button"
            data-testid="publish-edition"
            disabled={publish.isPending}
            onClick={() => publish.mutate()}
            className="mt-3 rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
          >
            {publish.isPending ? t.edition.publishing : t.edition.publish(p.next_number)}
          </button>
          {publish.isError && (
            <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
              {publish.error instanceof Error ? publish.error.message : t.edition.publishFailed}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function ChangeRow({ change, t }: { change: EditionChange; t: Messages }) {
  const what = [
    change.kind === "added" && t.edition.added,
    change.kind === "removed" && t.edition.removed,
    change.renamed && t.edition.renamed,
    change.moved && t.edition.moved,
    change.text && t.edition.text,
    change.audio && t.edition.audio,
    // Pictures are part of what publishing carries (ADR-0062), and a chapter
    // whose only change is one of these is in this list for that alone.
    !!change.pictures_added && t.edition.picturesAdded(change.pictures_added),
    !!change.pictures_removed && t.edition.picturesRemoved(change.pictures_removed),
    !!change.pictures_changed && t.edition.picturesEdited(change.pictures_changed),
  ].filter(Boolean);
  const warning =
    change.kind === "removed"
      ? null
      : change.no_audio
        ? t.edition.noAudio
        : change.audio_outdated
          ? t.edition.audioOutdated
          : null;

  return (
    <li
      data-testid="edition-change"
      data-kind={change.kind}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm"
    >
      <span
        dir="auto"
        className={`min-w-0 flex-1 truncate ${change.kind === "removed" ? "text-muted line-through" : ""}`}
      >
        {change.title}
      </span>
      <span className="text-xs text-muted">{what.join(" · ")}</span>
      {warning && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.7rem] text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
          {warning}
        </span>
      )}
    </li>
  );
}
